import React, { useState } from 'react';
import VerificationTab from "../components/VerificationTab";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../components/ui/tooltip';
import { 
  Globe, 
  Save,
  Loader2,
  Eye,
  Plus,
  Copy,
  Trash2} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { EmptyState } from '../components/common/EmptyState';
import { canWrite, canManageTeam, canManageBilling, isViewer } from '../lib/roleUtils';

const industries = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'import_export', label: 'Import/Export' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'retail', label: 'Retail' },
  { value: 'technology', label: 'Technology' },
  { value: 'services', label: 'Professional Services' },
  { value: 'construction', label: 'Construction' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'other', label: 'Other' }
];

const companyTypes = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'wholesaler', label: 'Wholesaler' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'service_provider', label: 'Service Provider' },
  { value: 'trading_company', label: 'Trading Company' },
  { value: 'agent', label: 'Agent/Broker' }
];

const employeeCounts = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' }
];

const countries = [
  'United States', 'Canada', 'Mexico', 'United Kingdom', 'Germany', 'France', 
  'Spain', 'Italy', 'China', 'Japan', 'South Korea', 'India', 'Brazil', 
  'Australia', 'Other'
];

export default function CompanyProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Profile state
  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get('/profiles/me')
  });

  const [formData, setFormData] = useState({});

  React.useEffect(() => {
    if (profile) {
      setFormData(profile);
    }
  }, [profile]);

  const updateProfile = useMutation({
    mutationFn: (data) => api.put('/profiles/me', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-profile']);
      toast.success('Profile updated');
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update profile');
    }
  });

  const handleSave = () => {
    updateProfile.mutate(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Team state (only for org owners)
  const isOrgOwner = user && !user.is_user;
  const canManageTeamMembers = canManageTeam(user);

  const { data: teamMembers, isLoading: teamLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
    enabled: !!isOrgOwner
  });

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', email: '', team_role: 'member', title: '' });
  const [teamErrors, setTeamErrors] = useState({});

  const inviteMutation = useMutation({
    mutationFn: (data) => api.post('/team/invite', data),
    onSuccess: (response) => {
      toast.success("Team member invited successfully!");
      const inviteUrl = `${window.location.origin}/team-invite/${response.token}`;
      setCreatedInviteUrl(inviteUrl);
      navigator.clipboard.writeText(inviteUrl);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send invite');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/team/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      toast.success("Team member removed");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to remove user");
    }
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/team/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      toast.success("Team member updated");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update member");
    }
  });

  const ROLE_OPTIONS = [
    { value: 'admin', label: 'Admin', desc: 'Manage team & settings' },
    { value: 'member', label: 'Member', desc: 'Full access to work' },
    { value: 'viewer', label: 'Viewer', desc: 'Read-only access' },
  ];

  const roleLabel = (role) => {
    if (role === 'owner') return 'Owner';
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.label : 'Member';
  };

  const resetTeamForm = () => {
    setTeamForm({ name: '', email: '', team_role: 'member', title: '' });
    setTeamErrors({});
    setCreatedInviteUrl('');
  };

  const handleTeamDialogClose = () => {
    if (createdInviteUrl) {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    }
    setTeamDialogOpen(false);
    resetTeamForm();
  };

  const validateTeamForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!teamForm.name || !teamForm.name.trim()) {
      newErrors.name = "Name is required";
    } else if (teamForm.name.length > 50) {
      newErrors.name = "Name must not exceed 50 characters";
    }
    if (!teamForm.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(teamForm.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setTeamErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTeamSubmit = (e) => {
    e.preventDefault();
    if (!validateTeamForm()) return;
    inviteMutation.mutate(teamForm);
  };

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold">{t("companyprofile.companyProfile")}</h1>
          <p className="text-muted-foreground">{t("companyprofile.manageYourBusinessProfile")}</p>
        </div>
        <div className="flex items-center gap-3">
          {formData.is_public && formData._id && (
            <Button variant="outline" asChild data-testid="preview-profile-btn">
              <a href={`/directory/${formData._id}`} target="_blank" rel="noopener noreferrer">
                <Eye className="h-4 w-4 mr-2" />
                {t("companyprofile.preview") || "Preview"}
              </a>
            </Button>
          )}
          <Button onClick={handleSave} disabled={updateProfile.isPending} data-testid="save-profile-btn">
            {updateProfile.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t("companyprofile.saveProfile") || "Save Profile"}
          </Button>
        </div>
      </div>

      {/* Visibility Toggle */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{t("companyprofile.publicProfile")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("companyprofile.makeVisible") || "Make your company visible in the directory"}
                </p>
              </div>
            </div>
            <Switch
              checked={formData.is_public || false}
              onCheckedChange={(checked) => updateField('is_public', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="basic">
        <TabsList>
          <TabsTrigger value="basic">
            {t("companyprofile.tab.basicInfo") || "Basic Info"}
          </TabsTrigger>
          <TabsTrigger value="details">
            {t("companyprofile.tab.details") || "Details"}
          </TabsTrigger>
          <TabsTrigger value="contact">
            {t("companyprofile.tab.contact") || "Contact"}
          </TabsTrigger>
          {canManageTeamMembers && (
            <TabsTrigger value="team">
              {t("companyprofile.tab.team") || "Team"}
            </TabsTrigger>
          )}
          <TabsTrigger value="verification">
            {t("companyprofile.tab.verification") || "Verification"}
          </TabsTrigger>
        </TabsList>

        {/* BASIC INFO TAB */}
        <TabsContent value="basic" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("companyprofile.basicInformation")}</CardTitle>
              <CardDescription>{t("companyprofile.essentialDetailsAboutYour")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.companyName")}</Label>
                  <Input
                    value={formData.company_name || ''}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    placeholder={t("companyprofile.ph.companyName") || "Your Company Inc."}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.tagline")}</Label>
                  <Input
                    value={formData.tagline || ''}
                    onChange={(e) => updateField('tagline', e.target.value)}
                    placeholder={t("companyprofile.ph.tagline") || "Quality products, global reach"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.industry")}</Label>
                  <Select
                    value={formData.industry || ''}
                    onValueChange={(value) => updateField('industry', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("companyprofile.ph.selectIndustry") || "Select industry"} />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.companyType")}</Label>
                  <Select
                    value={formData.company_type || ''}
                    onValueChange={(value) => updateField('company_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("companyprofile.selectType") || "Select type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("companyprofile.description")}</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder={t("companyprofile.describePlaceholder") || "Describe your company, products, and services..."}
                  rows={6}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DETAILS TAB */}
        <TabsContent value="details" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("companyprofile.companyDetails")}</CardTitle>
              <CardDescription>{t("companyprofile.additionalInformationAboutYour")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.yearEstablished")}</Label>
                  <Input
                    type="number"
                    value={formData.year_established || ''}
                    onChange={(e) => updateField('year_established', parseInt(e.target.value) || null)}
                    placeholder="2010"
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.numberOfEmployees")}</Label>
                  <Select
                    value={formData.employee_count || ''}
                    onValueChange={(value) => updateField('employee_count', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("companyprofile.ph.selectSize") || "Select size"} />
                    </SelectTrigger>
                    <SelectContent>
                      {employeeCounts.map(ec => (
                        <SelectItem key={ec.value} value={ec.value}>
                          {ec.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("companyprofile.certifications")}</Label>
                <Input
                  value={(formData.certifications || []).join(', ')}
                  onChange={(e) => updateField('certifications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder={t("companyprofile.ph.certifications") || "ISO 9001, CE, FDA (comma separated)"}
                />
                <p className="text-xs text-muted-foreground">{t("companyprofile.enterCertificationsSeparatedBy")}</p>
              </div>

              <div className="space-y-2">
                <Label>{t("companyprofile.languagesSpoken")}</Label>
                <Input
                  value={(formData.languages_spoken || []).join(', ')}
                  onChange={(e) => updateField('languages_spoken', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder={t("companyprofile.ph.languages") || "English, Spanish, Chinese"}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("companyprofile.shippingRegions")}</Label>
                <Input
                  value={(formData.shipping_regions || []).join(', ')}
                  onChange={(e) => updateField('shipping_regions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder={t("companyprofile.ph.exportRegions") || "North America, Europe, Asia"}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACT TAB */}
        <TabsContent value="contact" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("companyprofile.contactInformation")}</CardTitle>
              <CardDescription>{t("companyprofile.howPotentialPartnersCan")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.website")}</Label>
                  <Input
                    value={formData.website || ''}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.email")}</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.phone")}</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+1 555 123 4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.country")}</Label>
                  <Select
                    value={formData.country || ''}
                    onValueChange={(value) => updateField('country', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("companyprofile.ph.selectCountry") || "Select country"} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("companyprofile.city")}</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder={t("companyprofile.ph.city") || "Los Angeles"}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("companyprofile.address")}</Label>
                  <Input
                    value={formData.address || ''}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Business St"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEAM TAB (org owners only) */}
        {canManageTeamMembers && (
          <TabsContent value="team" className="mt-6">
            <div className="space-y-4">
              {/* Team header with invite button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{t("companyprofile.teamMembers")}</h2>
                  <p className="text-sm text-muted-foreground">{t("companyprofile.manageUsersWithinYour")}</p>
                </div>
                <Dialog open={teamDialogOpen} onOpenChange={(open) => {
                  if (!open) {
                    if (createdInviteUrl) {
                      queryClient.invalidateQueries({ queryKey: ['team'] });
                    }
                    resetTeamForm();
                  }
                  setTeamDialogOpen(open);
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="invite-team-button">
                      <Plus className="h-4 w-4 mr-2" />
                      {t("companyprofile.inviteTeamMember") || "Invite Team Member"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-lg">
                    <DialogHeader>
                      <DialogTitle>
                        {!createdInviteUrl ? t("companyprofile.inviteTeamMember") || "Invite Team Member" : t("companyprofile.invitationSent") || "Invitation Sent Successfully"}
                      </DialogTitle>
                      <DialogDescription>
                        {!createdInviteUrl
                          ? "Send an invitation to join your organization."
                          : "An invitation email has been sent. You can also share the link below."}
                      </DialogDescription>
                    </DialogHeader>

                    {!createdInviteUrl ? (
                      <div className="space-y-4 py-2">
                        <div className="space-y-2">
                          <Label htmlFor="team-name" className={teamErrors.name ? "text-destructive" : ""}>
                            {t("companyprofile.name") || "Name"} <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Input
                            id="team-name"
                            placeholder={t("companyprofile.ph.teamName") || "Team member's name"}
                            value={teamForm.name}
                            onChange={(e) => {
                              setTeamForm(prev => ({ ...prev, name: e.target.value }));
                              if (teamErrors.name) setTeamErrors(prev => ({ ...prev, name: null }));
                            }}
                            className={teamErrors.name ? "border-destructive" : ""}
                          />
                          {teamErrors.name && <p className="text-xs text-destructive mt-1">{teamErrors.name}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="team-email" className={teamErrors.email ? "text-destructive" : ""}>
                            {t("companyprofile.email") || "Email"} <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Input
                            id="team-email"
                            type="email"
                            placeholder="teammate@example.com"
                            value={teamForm.email}
                            onChange={(e) => {
                              setTeamForm(prev => ({ ...prev, email: e.target.value }));
                              if (teamErrors.email) setTeamErrors(prev => ({ ...prev, email: null }));
                            }}
                            className={teamErrors.email ? "border-destructive" : ""}
                          />
                          {teamErrors.email && <p className="text-xs text-destructive mt-1">{teamErrors.email}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="team-role">{t("companyprofile.role")}</Label>
                            <select
                              id="team-role"
                              value={teamForm.team_role}
                              onChange={(e) => setTeamForm(prev => ({ ...prev, team_role: e.target.value }))}
                              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            >
                              <option value="admin">{t("companyprofile.admin")}</option>
                              <option value="member">{t("companyprofile.member")}</option>
                              <option value="viewer">{t("companyprofile.viewer")}</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="team-title">{t("companyprofile.titleOptional")}</Label>
                            <Input
                              id="team-title"
                              placeholder="e.g. Sales Manager"
                              value={teamForm.title}
                              onChange={(e) => setTeamForm(prev => ({ ...prev, title: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 py-4 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-success/10 flex items-center justify-center">
                          <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="p-3 rounded-lg bg-muted font-mono text-sm break-all">
                          {createdInviteUrl}
                        </div>
                        <Button
                          variant="outline"
                          className="w-full mt-2"
                          onClick={() => {
                            navigator.clipboard.writeText(createdInviteUrl);
                            toast.success("Link copied!");
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Invitation Link
                        </Button>
                      </div>
                    )}

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={handleTeamDialogClose}>
                        Close
                      </Button>
                      {!createdInviteUrl && (
                        <Button
                          type="button"
                          onClick={handleTeamSubmit}
                          disabled={inviteMutation.isPending}
                        >
                          {inviteMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            "Generate Invite Link"
                          )}
                        </Button>
                      )}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Team table */}
              <Card>
                <CardContent className="p-0">
                  {teamLoading ? (
                    <div className="p-6 space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center gap-4">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-6 w-20" />
                        </div>
                      ))}
                    </div>
                  ) : teamMembers && teamMembers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table className="table-fixed w-full min-w-[700px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-left w-[30%] pl-10">{t("companyprofile.name")}</TableHead>
                            <TableHead className="text-center w-[30%]">{t("companyprofile.email")}</TableHead>
                            <TableHead className="text-center w-[12%]">{t("companyprofile.role")}</TableHead>
                            <TableHead className="text-center w-[18%]">{t("companyprofile.title")}</TableHead>
                            <TableHead className="text-center w-[10%]">{t("companyprofile.status")}</TableHead>
                            <TableHead className="text-center w-[10%]">{t("companyprofile.actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {teamMembers.map(member => (
                            <TableRow key={member._id} className="hover:bg-muted/50">
                              <TableCell>
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-10 h-10 flex-shrink-0 rounded-full bg-accent/10 flex items-center justify-center">
                                    <span className="font-semibold text-accent">
                                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-medium block truncate">
                                      {member.name} {member._id === (user?._id || user?.id) ? "(You)" : ""}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="truncate">{member.email}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                {member.team_role === 'owner' ? (
                                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">{t("companyprofile.owner")}</span>
                                ) : member.status === 'Invited' ? (
                                  <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">{roleLabel(member.team_role)}</span>
                                ) : (
                                  <select
                                    value={member.team_role || 'member'}
                                    onChange={(e) => updateMemberMutation.mutate({ id: member._id, data: { team_role: e.target.value } })}
                                    className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full border-0 cursor-pointer hover:bg-secondary/80 transition"
                                  >
                                    <option value="admin">{t("companyprofile.admin")}</option>
                                    <option value="member">{t("companyprofile.member")}</option>
                                    <option value="viewer">{t("companyprofile.viewer")}</option>
                                  </select>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {member.team_role !== 'owner' && member.status !== 'Invited' ? (
                                  <input
                                    type="text"
                                    value={member.title || ''}
                                    placeholder={t("companyprofile.ph.addTitle") || "Add title..."}
                                    className="text-xs text-center bg-transparent border-0 border-b border-transparent hover:border-muted-foreground/30 focus:border-primary focus:outline-none w-full transition"
                                    onBlur={(e) => {
                                      if (e.target.value !== (member.title || '')) {
                                        updateMemberMutation.mutate({ id: member._id, data: { title: e.target.value } });
                                      }
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    defaultValue={member.title || ''}
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">{member.title || '—'}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  member.status === 'Active' ? 'bg-success/20 text-success' :
                                  member.status === 'Invited' ? 'bg-warning/20 text-warning-foreground' :
                                  'bg-secondary text-secondary-foreground'
                                }`}>
                                  {member.status}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex justify-center items-center">
                                  {member.status === 'Invited' && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(`${window.location.origin}/team-invite/${member.invite_token}`);
                                            toast.success("Link copied!");
                                          }}
                                        >
                                          <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>{t("companyprofile.copyInviteLink")}</TooltipContent>
                                    </Tooltip>
                                  )}
                                  {member.is_user === true && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setUserToDelete(member._id);
                                            setDeleteDialogOpen(true);
                                          }}
                                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>{t("companyprofile.removeMember")}</TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="p-8">
                      <EmptyState
                        icon="user"
                        title="No Team Members Yet"
                        description="You are the only person in this organization. Invite team members to collaborate."
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("companyprofile.removeTeamMember")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove this user from your organization? They will immediately lose access to all deals and documents.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleteMutation.isPending}>{t("companyprofile.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground focus:ring-destructive"
                    onClick={() => { if (userToDelete) deleteMutation.mutate(userToDelete); }}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>
        )}
        <TabsContent value="verification" className="mt-6">
          <VerificationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
